import { logError } from '../utils/errorLogger';
import { unwrapPaginatedResponse, buildPaginationParams } from '../utils/paginatedApi';
import BaseApi from './BaseApi';
import HotelClientApi from './HotelClientApi';
import HotelFournisseurApi from './HotelFournisseurApi';

import moment from 'moment'
class FactureApi extends BaseApi {
  constructor() {
    super('factures');
  }

  // Get all factures
  async getAllFactures() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des factures');
      logError(error);
      throw error;
    }
  }

  async getFacturesPage({ page = 1, limit = 10, filters = {} } = {}) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}`, {
        params: {
          ...buildPaginationParams({ page, limit }),
          ...filters,
        },
      });
      return unwrapPaginatedResponse(response.data);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  // Get factures by status
  async getFacturesByStatus(statut) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/status/${statut}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des factures avec le statut ${statut}`);
      logError(error);
      throw error;
    }
  }

  // Get a single facture by ID
  async getFactureById(id) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Get factures by client
  async getFacturesByClient(idClient) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/client/${idClient}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des factures pour le client ${idClient}`);
      logError(error);
      throw error;
    }
  }

  // Get factures by client for payment allocation (only unpaid factures)
  async getFacturesByClientForPayment(idClient) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/client/${idClient}/for-payment`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la récupération des factures du client ${idClient} pour paiement`
      );
      logError(error);
      throw error;
    }
  }

  // Get factures by fournisseur
  async getFacturesByFournisseur(idFournisseur) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/fournisseur/${idFournisseur}`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la récupération des factures pour le fournisseur ${idFournisseur}`
      );
      logError(error);
      throw error;
    }
  }

  // Get factures by fournisseur for payment allocation (only unpaid factures)
  async getFacturesByFournisseurForPayment(idFournisseur) {
    try {
      const response = await this.getApiInstance(true).get(
        `/${this.endpoint}/fournisseur/${idFournisseur}/for-payment`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la récupération des factures du fournisseur ${idFournisseur} pour paiement`
      );
      logError(error);
      throw error;
    }
  }

  // Get ALL factures by client for edit mode (including fully paid ones)
  async getAllFacturesByClientForEdit(idClient) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/client/${idClient}/for-edit`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de toutes les factures du client ${idClient} pour édition`);
      logError(error);
      throw error;
    }
  }

  // Get ALL factures by fournisseur for edit mode (including fully paid ones)
  async getAllFacturesByFournisseurForEdit(idFournisseur) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/fournisseur/${idFournisseur}/for-edit`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de toutes les factures du fournisseur ${idFournisseur} pour édition`);
      logError(error);
      throw error;
    }
  }

  // Get factures by date range
  async getFacturesByDateRange(startDate, endDate) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/date-range`, {
        params: { startDate, endDate },
      });
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la récupération des factures pour la période ${startDate} - ${endDate}`
      );
      logError(error);
      throw error;
    }
  }

  // Get factures by type
  async getFacturesByType(typeFacture) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/type/${typeFacture}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des factures de type ${typeFacture}`);
      logError(error);
      throw error;
    }
  }

  // Create a new facture
  async createFacture(data) {
    try {
      const response = await this.getApiInstance(true).post(`/${this.endpoint}`, data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la facture');
      logError(error);
      throw error;
    }
  }

  // Update a facture
  async updateFacture(id, data) {
    try {
      const response = await this.getApiInstance(true).put(`/${this.endpoint}/${id}`, data);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la mise à jour de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Delete a facture
  async deleteFacture(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/${this.endpoint}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la suppression de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Update facture status
  async updateFactureStatus(id, statut, idUtilisateurValidation = null) {
    try {
      const response = await this.getApiInstance(true).patch(`/${this.endpoint}/${id}/status`, {
        statut,
        idUtilisateurValidation,
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du changement de statut de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Mark facture as sent
  async markFactureAsSent(id) {
    try {
      const response = await this.getApiInstance(true).patch(`/${this.endpoint}/${id}/send`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du marquage de la facture ${id} comme envoyée`);
      logError(error);
      throw error;
    }
  }

  // Calculate facture totals
  async calculateFactureTotals(id) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/${id}/totals`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du calcul des totaux de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Get facture statistics
  async getFactureStatistics() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/statistics`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques des factures');
      logError(error);
      throw error;
    }
  }

  // Get all clients for dropdown
  async getAllClients() {
    try {
      return await HotelClientApi.getAllClients();
    } catch (error) {
      console.error('Erreur lors de la récupération des clients');
      logError(error);
      throw error;
    }
  }

  // Get all fournisseurs for dropdown
  async getAllFournisseurs() {
    try {
      return await HotelFournisseurApi.getAllFournisseurs();
    } catch (error) {
      console.error('Erreur lors de la récupération des fournisseurs');
      logError(error);
      throw error;
    }
  }

  // ===== NOUVELLES FONCTIONNALITÉS =====

  // Marquer une facture comme brouillon
  async markFactureAsDraft(id, isDraft = true) {
    try {
      const response = await this.getApiInstance(true).patch(`/${this.endpoint}/${id}/draft`, {
        isDraft,
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du changement de statut brouillon de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Valider une facture
  async validateFacture(id) {
    try {
      const response = await this.getApiInstance(true).patch(`/${this.endpoint}/${id}/validate`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la validation de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Annuler la validation d'une facture
  async unvalidateFacture(id) {
    try {
      const response = await this.getApiInstance(true).patch(`/${this.endpoint}/${id}/unvalidate`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de l'annulation de la validation de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Obtenir les factures brouillon
  async getDraftFactures() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/filter/draft`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des factures brouillon');
      logError(error);
      throw error;
    }
  }

  // Obtenir les factures validées
  async getValidatedFactures() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/filter/validated`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des factures validées');
      logError(error);
      throw error;
    }
  }

  // Obtenir les factures avec PDF
  async getFacturesWithPdf() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/filter/with-pdf`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des factures avec PDF');
      logError(error);
      throw error;
    }
  }

  // ===== FONCTIONNALITÉS PDF =====

  // Générer un PDF pour une facture
  async generateFacturePdf(id) {
    try {
      const response = await this.getApiInstance(true).post(`/${this.endpoint}/${id}/pdf/generate`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la génération du PDF pour la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Télécharger un PDF de facture
  async downloadFacturePdf(id) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/${id}/pdf/download`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du téléchargement du PDF de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Visualiser un PDF de facture
  async viewFacturePdf(id) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/${id}/pdf/view`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la visualisation du PDF de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Régénérer un PDF de facture
  async regenerateFacturePdf(id) {
    try {
      const response = await this.getApiInstance(true).post(
        `/${this.endpoint}/${id}/pdf/regenerate`
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la régénération du PDF pour la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Supprimer un PDF de facture
  async deleteFacturePdf(id) {
    try {
      const response = await this.getApiInstance(true).delete(`/${this.endpoint}/${id}/pdf`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la suppression du PDF de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // Obtenir les informations du PDF d'une facture
  async getFacturePdfInfo(id) {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/${id}/pdf/info`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des informations PDF de la facture ${id}`);
      logError(error);
      throw error;
    }
  }

  // ===== MÉTHODES UTILITAIRES =====

  validateFactureData(data) {
    const errors = [];

    if (!data.type_facture) {
      errors.push('Le type de facture est requis');
    }

    if (!data.numero_facture) {
      errors.push('Le numéro de facture est requis');
    }

    if (data.type_facture === 'client' && !data.id_client) {
      errors.push('Un client doit être sélectionné pour une facture client');
    }

    if (data.type_facture === 'fournisseur' && !data.id_fournisseur) {
      errors.push('Un fournisseur doit être sélectionné pour une facture fournisseur');
    }

    if (!data.id_utilisateur_creation) {
      errors.push("L'utilisateur de création est requis");
    }

    if (data.total_ht && data.total_ht < 0) {
      errors.push('Le total HT doit être un nombre positif');
    }

    if (data.total_tva && data.total_tva < 0) {
      errors.push('Le total TVA doit être un nombre positif');
    }

    if (data.total_ttc && data.total_ttc < 0) {
      errors.push('Le total TTC doit être un nombre positif');
    }

    if (data.total_ttc_produit && data.total_ttc_produit < 0) {
      errors.push('Le total TTC produit doit être un nombre positif');
    }

    if (data.taux_change && data.taux_change <= 0) {
      errors.push('Le taux de change doit être un nombre positif');
    }

    if (data.marge && data.marge < 0) {
      errors.push('La marge doit être un nombre non négatif');
    }

    if (data.prix_achat && data.prix_achat < 0) {
      errors.push('Le prix d\'achat doit être un nombre non négatif');
    }

    if (data.date_facture && data.date_echeance && data.date_facture > data.date_echeance) {
      errors.push("La date de facture ne peut pas être postérieure à la date d'échéance");
    }

    return {
      isValid: errors.length === 0,
      errors: errors,
    };
  }

  formatFactureForDisplay(facture) {
    const nom = (facture.client_nom || '').trim();
    const prenom = (facture.client_prenom || '').trim();
    const clientDisplay =
      nom || prenom
        ? [nom, prenom].filter(Boolean).join(' ')
        : 'Non spécifié';
    const fournisseurDisplay = facture.fournisseur_nom || 'Non spécifié';

    return {
      ...facture,
      displayName: facture.numero_facture,
      displayType: this.getTypeDisplay(facture.type_facture),
      displayStatus: this.getStatusDisplay(facture.statut),
      displayAmount: new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: facture.devise || 'TND',
      }).format(facture.total_ttc || 0),
      displayDate: facture.date_facture
        ? moment(facture.date_facture).format('DD/MM/YYYY')
        : '-',
      displayDueDate: facture.date_echeance
        ? moment(facture.date_echeance).format('DD/MM/YYYY')
        : '-',
      client_display: clientDisplay,
      fournisseur_display: fournisseurDisplay,
    };
  }

  getStatusDisplay(statut) {
    const statusMap = {
      solde: 'Soldée',
      non_solde: 'Non soldée',
      partiellement_solde: 'Partiellement soldée',
      brouillon: 'Brouillon',
      validee: 'Validée',
      envoyee: 'Envoyée',
      annulee: 'Annulée',
    };
    return statusMap[statut] || statut;
  }

  getTypeDisplay(type) {
    const typeMap = {
      client: 'Client',
      fournisseur: 'Fournisseur',
    };
    return typeMap[type] || type;
  }

  getStatusColor(statut) {
    const colorMap = {
      solde: 'green',
      non_solde: 'red',
      partiellement_solde: 'yellow',
      brouillon: 'gray',
      validee: 'blue',
      envoyee: 'purple',
      annulee: 'gray',
    };
    return colorMap[statut] || 'gray';
  }

  getCommonFactureTypes() {
    return [
      { value: 'client', label: 'Facture Client' },
      { value: 'fournisseur', label: 'Facture Fournisseur' },
    ];
  }

  getCommonCurrencies() {
    return [
      { value: 'TND', label: 'Dinar Tunisien (TND)' },
      { value: 'EUR', label: 'Euro (EUR)' },
      { value: 'USD', label: 'Dollar US (USD)' },
      { value: 'GBP', label: 'Livre Sterling (GBP)' },
    ];
  }

  // Get currencies from pays table
  async getCurrenciesFromPays() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/pays/currencies`);
      // Transform the response to match the expected format
      const currencies = response.data.map(currency => ({
        value: currency.currency,
        label: `${currency.currency}`,
      }));
      return currencies;
    } catch (error) {
      logError(error);
      // Fallback to common currencies if API fails
      return this.getCommonCurrencies();
    }
  }

  getCommonStatuses() {
    return [
      { value: 'non_solde', label: 'Non soldée' },
      { value: 'partiellement_solde', label: 'Partiellement soldée' },
      { value: 'solde', label: 'Soldée' },
    ];
  }

  calculateTotalsFromLines(lines = []) {
    const totals = {
      total_ht: 0,
      total_tva: 0,
      total_ttc: 0,
    };

    lines.forEach(line => {
      const ht = parseFloat(line.montant_ht || 0);
      const tva = parseFloat(line.montant_tva || 0);
      const ttc = parseFloat(line.montant_ttc || 0);

      totals.total_ht += ht;
      totals.total_tva += tva;
      totals.total_ttc += ttc;
    });

    return totals;
  }

  async generateFactureNumber(typeFacture = 'client') {
    try {
      // Logique pour générer un numéro de facture unique
      const timestamp = moment().valueOf();
      const prefix = typeFacture === 'client' ? 'FC' : 'FF';
      return `${prefix}${timestamp}`;
    } catch (error) {
      console.error('Erreur lors de la génération du numéro de facture');
      logError(error);
      throw error;
    }
  }

  async generateHtmlBill(id, language = 'fr') {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/${id}/html?lang=${language}`);
      return response.data;
    } catch (error) {
      console.error('Error generating HTML bill:', error);
      throw error;
    }
  }

  async downloadSummaryPdf() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/summary-pdf`, {
        responseType: 'blob',
      });
      return response;
    } catch (error) {
      console.error(`Erreur lors du téléchargement du PDF de résumé`);
      logError(error);
      throw error;
    }
  }

  // Get hotel contacts for a reservation
  async getHotelContactsForReservation(reservationId) {
    try {
      const response = await this.getApiInstance(true).get(
        `/hotel-reservations/${reservationId}/hotel-contacts`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Erreur lors de la récupération des contacts pour la réservation ${reservationId}`
      );
      logError(error);
      throw error;
    }
  }

  // Get hotel contacts for a hotel by hotelId
  async getHotelContactsByHotelId(hotelId) {
    try {
      const response = await this.getApiInstance(true).get(
        `/hotels/${hotelId}/contacts?reservation=true`
      );
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des contacts pour l'hôtel ${hotelId}`);
      logError(error);
      throw error;
    }
  }
  // Get all hotel names
  async getAllhotelNames() {
    try {
      const response = await this.getApiInstance(true).get(`/${this.endpoint}/hotel/names`);
      return response.data;
    } catch (error) {
      console.error(`Erreur lors de la récupération des noms d'hôtels`);
      logError(error);
      throw error;
    }
  }
}

export default new FactureApi();
